// @flow

import React, {Component} from 'react'
import {observer} from 'mobx-react'
import {isEmpty, get} from 'lodash'

import store from '../store'
import api from '../../../src/api/client'
import * as searchHelpers from '../helpers'
import Header from '../../common/components/Header'
import Main from './Main'
import Footer from '../../common/components/Footer'
import css from './Homepage.css'

const PAGE_SIZE = 10

type State = {
  showQrCodeText: boolean,
  keyword: ?string,
  isPaginationInUse: boolean,
}

@observer
class Homepage extends Component {
  state: State = {
    showQrCodeText: false,
    keyword: '',
    isPaginationInUse: true,
  }

  componentWillMount() {
    this.getSettings()
    this.getFooterLabels()
    const {pathname} = window.location
    if (pathname !== '/') {
      this.getMedicineList()
    }
  }

  componentWillReceiveProps() {
    this.getSettings()
    this.getFooterLabels()
    const {pathname} = window.location
    if (pathname !== '/') {
      this.getMedicineList()
    }
  }

  getSettings = async () => {
    // Fetch footer label data only if empty
    if (isEmpty(store.settings)) {
      const settings = await api.resources.settings.getSettings()
      if (!isEmpty(settings)) {
        const settingsObj = {
          showMedicineInfo: settings.showMedicineInfo,
        }
        store.saveSettings(settingsObj)
      }
    }
  }

  getFooterLabels = async () => {
    // Fetch footer label data only if empty
    if (isEmpty(store.footerLabels)) {
      store.saveIsFetching(true)
      const footerLabels = await api.resources.footer.getFooterLabels()
      if (!isEmpty(footerLabels)) {
        const footerLabelObj = {
          searchTitle: footerLabels.search_title,
          searchDescription: footerLabels.search_description,
          searchPlaceholder: footerLabels.search_placeholder,
          searchInstruction: footerLabels.search_instruction,
          company: footerLabels.footer_company,
          description: footerLabels.footer_description,
          phone: footerLabels.footer_phone,
          copyright: footerLabels.footer_copyright,
          queueInfo: footerLabels.footer_queueInfo,
        }
        store.saveFooterLabels(footerLabelObj)
      }
    }
  }

  getMedicineList = async () => {
    const {pathname, search} = window.location

    switch (pathname) {
      case '/haku':
        const keyword = searchHelpers.getSearchParameter(search, 'hakusana')
        this.setState({keyword: keyword, isPaginationInUse: true})
        store.saveIsFetching(true)
        this.getSearchItemsByKeyword(keyword)
        break
      case '/laakityskortti':
        const vnrs = searchHelpers.getSearchParameter(search, 'vnr')
        this.setState({isPaginationInUse: false})
        store.saveIsFetching(true)
        this.getSearchItemsByVnrs(vnrs)
        break
      default:
    }
  }

  getSearchItemsByKeyword = async (keyword: ?string) => {
    try {
      if (keyword) {
        const filteredMedicines = await api.resources.medicine.getMedicinesByKeyword(keyword)
        const pagedMedicines = []
        const itemsLength = filteredMedicines.length
        for (var i = 0; i < PAGE_SIZE; i++) {
          if (i >= itemsLength) {
            break
          } else {
            pagedMedicines.push(filteredMedicines[i])
          }
        }

        store.saveActivePage(1)
        store.saveFilteredMedicines(filteredMedicines)
        store.savePagedMedicines(pagedMedicines)
        store.saveIsFetching(false)
      } else {
        store.saveIsFetching(false)
      }
    } catch (e) {
      console.log('Error: ', e)
    }
  }
  getSearchItemsByVnrs = async (vnrs: ?string) => {
    try {
      if(vnrs) {
        const filteredMedicines = []
        const tempMedicines = await api.resources.medicine.getMedicinesByVnrs(vnrs)
        const vnrArray = vnrs.split(',')
        for (let i = 0; i < vnrArray.length; i++) {
          const medicine = tempMedicines.filter((item) => {
            return item.vnr === vnrArray[i]
          })
          if(medicine.length > 0) {
            filteredMedicines.push(medicine[0])
          } else {
            filteredMedicines.push({vnr: vnrArray[i], isMissing: true})
          }
        }
        const pagedMedicines = []
        const itemsLength = filteredMedicines.length
        for (var j = 0; j < PAGE_SIZE; j++) {
          if (j >= itemsLength) {
            break
          } else {
            pagedMedicines.push(filteredMedicines[j])
          }
        }
        store.saveActivePage(1)
        store.saveFilteredMedicines(filteredMedicines)
        store.savePagedMedicines(pagedMedicines)
        store.saveIsFetching(false)
        this.setState({
          showQrCodeText: true,
        })
      } else {
        store.saveIsFetching(false)
      }

    } catch (e) {
      console.log('Error: ', e)
    }
  }

  loadMore = () => {
    const {pagedMedicines, filteredMedicines, activePage} = store

    const itemsLength = filteredMedicines.length
    for (let i = activePage * 10; i < (activePage + 1) * PAGE_SIZE; i++) {
      if (i >= itemsLength) {
        break
      } else {
        pagedMedicines.push(filteredMedicines[i])
      }
    }
    store.savePagedMedicines(pagedMedicines)
    store.saveActivePage(activePage + 1)
  }

  handleSearch = (keyword: string) => {
    if (keyword) {
      this.props.history.push(`/haku?hakusana=${keyword}`)
    }
  }

  handleKeywordChange = (value: string) => {
    this.setState({keyword: value})
  }

  render() {
    const pathname = window.location.pathname

    const {filteredMedicines, footerLabels, pagedMedicines, isFetching, settings} = store
    const {searchTitle, searchDescription, searchPlaceholder, searchInstruction} = footerLabels

    const showMedicineInfo = get(settings, 'showMedicineInfo') ? (get(settings, 'showMedicineInfo') ? true : false) : false

    const {isPaginationInUse, keyword, showQrCodeText} = this.state

    const {search} = window.location
    const urlKeyword = searchHelpers.getSearchParameter(search, 'hakusana')

    return (
      <div className={css.component}>
        <Header
          showSearch={pathname !== '/'}
          onSearch={(keyword) => this.handleSearch(keyword)}
          searchPlaceholder={searchPlaceholder}
          keyword={keyword}
          onKeywordChange={value => this.handleKeywordChange(value)}
        />
        <Main
          titleText={searchTitle}
          descriptionText={searchDescription}
          placeholderText={searchPlaceholder}
          instructionText={searchInstruction}
          isFetching={isFetching}
          medicineAmount={filteredMedicines ? filteredMedicines.length : 0}
          medicineItems={isPaginationInUse ? pagedMedicines : filteredMedicines}
          showLoadMore={isPaginationInUse && (filteredMedicines.length > pagedMedicines.length)}
          onLoadMore={() => this.loadMore()}
          showSearch={pathname === '/'}
          onSearch={(keyword) => this.handleSearch(keyword)}
          showQrCodeText={showQrCodeText}
          keyword={keyword}
          onKeywordChange={value => this.handleKeywordChange(value)}
          showMedicineList={pathname !== '/'}
          showMedicineInfo={showMedicineInfo}
          urlKeyword={urlKeyword}
        />
        <Footer
          company={get(footerLabels, 'company')}
          description={get(footerLabels, 'description')}
          phone={get(footerLabels, 'phone')}
          queueInfo={get(footerLabels, 'queueInfo')}
          copyright={get(footerLabels, 'copyright')}
        />
      </div>
    )
  }
}

export default Homepage



// WEBPACK FOOTER //
// ./src/homepage/components/Homepage.js